import React, { useEffect, useState } from 'react';
import { getServers, createServer, deleteServer, updateServer, Server } from '../api/admin';
import styles from './CommonPage.module.css';
import { Trash2, Plus, Server as ServerIcon, Edit, AlertCircle } from 'lucide-react';

const ServerPage: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ idServerIp: '', idServerKey: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);


  useEffect(() => {
    loadServers();
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen]);

  const loadServers = async () => {
    try {
      const data = await getServers();
      setServers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    if (servers.length >= 1) {
      alert('서버는 하나만 설정할 수 있습니다. 이미 등록된 서버를 삭제 후 추가해주세요.');
      return;
    }
    setFormData({ idServerIp: '', idServerKey: '' });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (server: Server) => {
    setFormData({
      idServerIp: server.idServerIp,
      idServerKey: server.idServerKey || ''
    });
    setEditingId(server.id);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateServer(editingId, formData);
      } else {
        await createServer(formData);
      }
      setIsModalOpen(false);
      loadServers();
    } catch (e) {
      alert('작업 실패');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 이 서버를 삭제하시겠습니까?')) return;
    try {
      await deleteServer(id);
      loadServers();
    } catch (e) {
      alert('삭제 실패');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>ID/Relay 서버 관리</h1>
          <p>클라이언트 연결을 위한 서버를 관리합니다.</p>
        </div>
        <button 
          className={`${styles.primaryBtn} ${servers.length >= 1 ? styles.disabled : ''}`} 
          onClick={handleAddClick}
          style={{ opacity: servers.length >= 1 ? 0.5 : 1, cursor: servers.length >= 1 ? 'not-allowed' : 'pointer' }}
        >
          <Plus size={16} /> 서버 추가
        </button>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2>{editingId ? '서버 수정' : '서버 추가'}</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>ID 서버 IP / 호스트</label>
                <input
                  value={formData.idServerIp}
                  onChange={e => setFormData({ ...formData, idServerIp: e.target.value })}
                  required
                  placeholder="예: 192.168.1.100"
                />
              </div>
              <div className={styles.formGroup}>
                  <label>ID 서버 키</label>
                  <input
                    value={formData.idServerKey}
                    onChange={e => setFormData({ ...formData, idServerKey: e.target.value })}
                    placeholder="공개 키 (선택 사항)"
                  />
                </div>
              <div className={styles.formActions}>
                <button type="button" onClick={() => setIsModalOpen(false)} className={styles.cancelBtn}>취소</button>
                <button type="submit" className={styles.submitBtn}>{editingId ? '저장' : '추가'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.grid}>
        {servers.map(server => (
          <div key={server.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ServerIcon size={20} color="#2563eb" />
                <h3>{server.idServerIp}</h3>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleEditClick(server)} className={styles.iconBtn} title="수정">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(server.id)} className={styles.deleteBtn} title="삭제">
                    <Trash2 size={16} />
                  </button>
              </div>
            </div>
            <div className={styles.cardBody}>
              <div style={{ marginBottom: '4px' }}><strong>ID Server IP:</strong> {server.idServerIp}</div>
              <div className={styles.truncate} title={server.idServerKey || '키 없음'}>
                <strong>Key:</strong> {server.idServerKey || '(키 없음)'}
              </div>
            </div>
          </div>
        ))}
        {servers.length === 0 && !loading && (
            <div className={styles.emptyState}>
                <AlertCircle size={48} color="#ccc" />
                <p>설정된 서버가 없습니다.</p>
            </div>
        )}
      </div>
    </div>
  );
};

export default ServerPage;
